import { booleanAttribute, Component, HostBinding, Input } from '@angular/core';
import { Border, borderToStyle } from 'shared/design-system/data-access/border';
import {
    CalcSize,
    FlexSize,
    MinSize,
    PaddingSize,
    PercentSize,
    PxSize,
    RemSize,
    VarSize,
} from 'shared/design-system/data-access/layout';
import { BreakpointEnum } from 'shared/design-system/data-access/responsive';
import { ColorEnum } from 'shared/design-system/data-access/theme';

@Component({
    selector: 'it-row',
    imports: [],
    templateUrl: './row.component.html',
    styleUrls: ['./row.component.scss'],
})
export class RowComponent {
    @Input() axisX?:
        | 'flex-start'
        | 'center'
        | 'flex-end'
        | 'space-between'
        | 'space-evenly';
    @Input() axisY?: 'flex-start' | 'center' | 'stretch';
    @Input() flex?: FlexSize;
    @Input() gap?: RemSize = '2rem';
    @Input() padding?: PaddingSize | PxSize = '2rem';
    @Input() backgroundColor?: ColorEnum;
    @Input() color?: ColorEnum;
    @Input() border?: Border;
    @Input() borderRadius?: PxSize | RemSize;
    @Input() margin?: PaddingSize;
    @Input() wrap?: 'wrap' | 'no-wrap';
    @Input({ transform: booleanAttribute }) forceWrap?: boolean;
    @Input() clampWidth?: PxSize | BreakpointEnum | VarSize | MinSize;
    @Input() minWidth?: VarSize | CalcSize | RemSize | PxSize;
    @Input() minHeight?: VarSize | CalcSize | RemSize;
    @Input() height?: VarSize | CalcSize | RemSize | PxSize;
    @Input() width?: PercentSize | PxSize | VarSize;
    @Input() clampHeight?:
        | PxSize
        | BreakpointEnum
        | VarSize
        | MinSize
        | CalcSize;

    @HostBinding('style.flex-direction')
    get flexDirection() {
        return this.forceWrap ? 'column' : 'row';
    }

    @HostBinding('style.justify-content')
    get justifyContent() {
        return this.axisX;
    }

    @HostBinding('style.align-items')
    get alignItems() {
        return this.axisY;
    }

    @HostBinding('style.flex')
    get flexStyle() {
        return this.flex;
    }

    @HostBinding('style.gap')
    get gapStyle() {
        return this.gap;
    }

    @HostBinding('style.width')
    get widthStyle() {
        return this.clampWidth ? `min(${this.clampWidth},100%)` : this.width;
    }

    @HostBinding('style.padding')
    get paddingStyle() {
        return this.padding;
    }

    @HostBinding('style.background-color')
    get backgroundColorStyle() {
        return this.backgroundColor;
    }

    @HostBinding('style.color')
    get colorStyle() {
        return this.color;
    }

    @HostBinding('style.border-top')
    get borderTop() {
        return borderToStyle(this.border)?.borderTop;
    }

    @HostBinding('style.border-right')
    get borderRight() {
        return borderToStyle(this.border)?.borderRight;
    }

    @HostBinding('style.border-bottom')
    get borderBottom() {
        return borderToStyle(this.border)?.borderBottom;
    }

    @HostBinding('style.border-left')
    get borderLeft() {
        return borderToStyle(this.border)?.borderLeft;
    }

    @HostBinding('style.border-radius')
    get borderRadiusStyle() {
        return this.borderRadius;
    }

    @HostBinding('style.margin')
    get marginStyle() {
        return this.margin;
    }

    @HostBinding('style.min-width')
    get minWidthStyle() {
        return this.minWidth;
    }

    @HostBinding('style.min-height')
    get minHeightStyle() {
        return this.minHeight;
    }

    @HostBinding('style.height')
    get heightStyle() {
        return this.clampHeight ? `min(${this.clampHeight},100%)` : this.height;
    }

    @HostBinding('style.max-width')
    get maxWidthStyle() {
        return this.clampWidth;
    }

    @HostBinding('style.max-height')
    get maxHeightStyle() {
        return this.clampHeight;
    }

    @HostBinding('style.flex-wrap')
    get flexWrapStyle() {
        return this.wrap;
    }
}
